import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { importFarmerOrchardCsv } from "@/lib/api";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";

export default function AdminImport() {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f && f.name.toLowerCase().endsWith(".csv")) setFile(f);
    else if (f) toast.error("Please choose a .csv file");
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const r = await importFarmerOrchardCsv(file);
      setResult(r);
      toast.success(`Imported ${r.imported} farmers, skipped ${r.skipped_duplicates}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="admin-import">
      <div className="flex items-center gap-3 mb-6">
        <FileSpreadsheet className="w-6 h-6 text-[var(--tr-deep-green)]" />
        <h2 className="tr-section-title text-3xl">CSV Import</h2>
      </div>

      <div className="tr-card p-6 lg:p-8 mb-6">
        <h3 className="font-display font-extrabold text-lg text-[var(--tr-deep-green)]">Google Form → TrueRoots One</h3>
        <p className="mt-2 text-[var(--tr-muted)] leading-relaxed text-sm">
          Export the responses of the official <b>Farmer &amp; Orchard Registration Form</b> from Google Sheets as a
          CSV file, then drop it here. Farmer profiles and their orchards will be created automatically. Duplicate
          mobile numbers are skipped so you can re-run this any time.
        </p>
        <ol className="mt-4 space-y-2 text-sm text-[var(--tr-deep-green)] list-decimal list-inside">
          <li>Google Sheets → File → Download → Comma-separated values (.csv)</li>
          <li>Upload below</li>
          <li>Review the import summary</li>
        </ol>
      </div>

      <div className="tr-card p-6 lg:p-8">
        <div className="border-2 border-dashed border-[rgba(6,78,59,0.25)] rounded-2xl p-8 text-center bg-[var(--tr-bone)]">
          <Upload className="w-8 h-8 text-[var(--tr-deep-green)] mx-auto" />
          <div className="mt-3 font-display font-extrabold text-xl text-[var(--tr-deep-green)]">
            {file ? file.name : "Choose a CSV file"}
          </div>
          <p className="mt-1 text-sm text-[var(--tr-muted)]">Maximum size: 5 MB. UTF-8 encoded.</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="admin-import-file"
            onChange={onPick}
          />
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              data-testid="admin-import-pick"
              onClick={() => fileInputRef.current?.click()}
              className="tr-pill-btn tr-btn-outline"
            >
              Select CSV file
            </button>
            <button
              type="button"
              data-testid="admin-import-submit"
              disabled={!file || uploading}
              onClick={submit}
              className="tr-pill-btn tr-btn-zest disabled:opacity-50"
            >
              {uploading ? "Importing…" : "Start import"}
            </button>
          </div>
        </div>

        {result && (
          <div data-testid="admin-import-result" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ResultTile ok label="Imported" value={result.imported} />
              <ResultTile label="Skipped (duplicates)" value={result.skipped_duplicates} />
              <ResultTile warn label="Errors" value={result.errors.length} />
            </div>

            {result.sample?.length > 0 && (
              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--tr-muted)] font-bold mb-2">
                  Sample of newly created farmers
                </div>
                <div className="tr-card overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--tr-sage)]/50">
                      <tr>
                        <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tr-deep-green)]">Farmer ID</th>
                        <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tr-deep-green)]">Name</th>
                        <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tr-deep-green)]">Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.sample.map((s) => (
                        <tr key={s.farmer_code} className="border-t border-[rgba(6,78,59,0.08)]">
                          <td className="px-4 py-2 font-mono font-semibold text-[var(--tr-deep-green)]">{s.farmer_code}</td>
                          <td className="px-4 py-2">{s.name}</td>
                          <td className="px-4 py-2">{s.phone}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--tr-zest)] font-bold mb-2">
                  Row errors (fix and re-upload)
                </div>
                <ul className="space-y-1 text-sm text-[var(--tr-muted)] bg-[var(--tr-bone)] rounded-xl p-4 max-h-64 overflow-y-auto">
                  {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.reason}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ResultTile({ label, value, ok, warn }) {
  const tone = ok ? "text-[var(--tr-lime)]" : warn ? "text-[var(--tr-zest)]" : "text-[var(--tr-deep-green)]";
  const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : FileSpreadsheet;
  return (
    <div className="tr-card p-5">
      <Icon className={`w-5 h-5 ${tone}`} />
      <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[var(--tr-muted)] font-bold">{label}</div>
      <div className={`mt-1 font-display font-extrabold text-3xl ${tone}`}>{value}</div>
    </div>
  );
}
